import clsx from 'clsx';
import { forwardRef } from 'react';

import {
  FocusScope,
  _mergeProps,
  _useAriaLabelingProps as useAriaLabelingProps,
} from '@dynatrace/strato-components/core';
import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
  type AriaLabelingProps,
} from '@dynatrace/strato-components/core';

import { overlayCSS } from './Overlay.sty.js';
import { OverlayContainer } from '../../core/components/OverlayContainer.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { OverlayStackingProvider } from '../../core/providers/OverlayStackingProvider.js';
import type { OverlayProps } from '../hooks/useOverlayWithTrigger.js';

/**
 * @public
 */
export interface OverlayComponentProps
  extends AriaLabelingProps,
    WithChildren,
    DataTestId,
    StylingProps,
    MaskingProps {
  /** Whether the overlay is open. */
  isOpen: OverlayProps['overlayState']['isOpen'];
  /**
   * Properties for connecting the overlay to the overlay trigger.
   * Includes styles and accessibility attributes.
   */
  overlayProps?: OverlayProps['overlayProps'];
}

/**
 * The `Overlay` component allows you to show additional content when the user
 * clicks on a particular element in the UI. The `Overlay` is placed
 * at the border of its trigger element. To define a UI element as
 * a trigger, use the `useOverlayWithTrigger` hook. The Overlay only
 * exists in a controlled manner, so the `isOpen` prop must be
 * updated by the developer.
 * @public
 */
export const Overlay = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  OverlayComponentProps
>((props, forwardedRef) => {
  const {
    isOpen,
    children,
    overlayProps,
    className: customClassName,
    style: customStyles,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;
  const ariaProps = useAriaLabelingProps(remainingProps);

  const overlayContainerProps = useOverlayStacking(undefined, 'dropdown');
  const { trapFocus, ...remainingOverlayProps } = overlayProps ?? {};

  if (!isOpen) {
    return null;
  }

  return (
    <OverlayContainer
      data-testid={dataTestId}
      {...overlayContainerProps}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
    >
      <FocusScope contain={trapFocus} autoFocus>
        <div
          ref={forwardedRef}
          className={clsx(overlayCSS, customClassName)}
          style={customStyles}
          {..._mergeProps(remainingOverlayProps, ariaProps)}
        >
          <OverlayStackingProvider>{children}</OverlayStackingProvider>
        </div>
      </FocusScope>
    </OverlayContainer>
  );
});

(Overlay as typeof Overlay & { displayName: string }).displayName = 'Overlay';
